import { useGifStore } from './gif-store';
import { TextOverlay, TextPositionPreset, TextFontFamily } from './types';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Type, Plus, Trash2 } from 'lucide-react';

const POSITION_PRESETS: { value: TextPositionPreset; label: string }[] = [
  { value: 'top-center', label: 'Top' },
  { value: 'center', label: 'Center' },
  { value: 'bottom-center', label: 'Bottom' },
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
];

const FONT_FAMILIES: { value: TextFontFamily; label: string }[] = [
  { value: 'sans-serif', label: 'Sans Serif' },
  { value: 'serif', label: 'Serif' },
  { value: 'monospace', label: 'Monospace' },
];

/**
 * F4: Text/Caption overlay editor for GIFs.
 * Supports up to 3 text layers, each with content, position, font, color, stroke.
 */
export function TextOverlayEditor() {
  const overlays = useGifStore((s) => s.settings.textOverlays || []);
  const addTextOverlay = useGifStore((s) => s.addTextOverlay);
  const updateTextOverlay = useGifStore((s) => s.updateTextOverlay);
  const removeTextOverlay = useGifStore((s) => s.removeTextOverlay);

  const handleAdd = () => {
    addTextOverlay({
      content: 'Caption',
      position: { kind: 'preset', preset: 'bottom-center' },
      fontSize: 32,
      fontFamily: 'sans-serif',
      color: '#ffffff',
      strokeColor: '#000000',
      strokeWidth: 2,
      frameRange: 'all',
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Type className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Text Overlays</CardTitle>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleAdd}
            disabled={overlays.length >= 3}
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            Add Text
          </Button>
        </div>
        <CardDescription>
          Add captions burned into your GIF. Up to 3 layers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {overlays.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No text overlays. Click "Add Text" to add a caption.
          </p>
        ) : (
          overlays.map((overlay, index) => (
            <TextOverlayItem
              key={overlay.id}
              overlay={overlay}
              index={index}
              onUpdate={(updates) => updateTextOverlay(overlay.id, updates)}
              onRemove={() => removeTextOverlay(overlay.id)}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

function TextOverlayItem({
  overlay,
  index,
  onUpdate,
  onRemove,
}: {
  overlay: TextOverlay;
  index: number;
  onUpdate: (updates: Partial<TextOverlay>) => void;
  onRemove: () => void;
}) {
  const positionPreset =
    overlay.position.kind === 'preset' ? overlay.position.preset : 'bottom-center';

  return (
    <div className="rounded-lg border border-border p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Text {index + 1}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={onRemove}
          aria-label="Remove text overlay"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div>
        <Label htmlFor={`text-${overlay.id}`} className="text-xs">Content</Label>
        <Input
          id={`text-${overlay.id}`}
          value={overlay.content}
          onChange={(e) => onUpdate({ content: e.target.value })}
          placeholder="Caption text"
          maxLength={120}
          className="mt-1"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Position</Label>
          <Select
            value={positionPreset}
            onValueChange={(v) =>
              onUpdate({ position: { kind: 'preset', preset: v as TextPositionPreset } })
            }
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {POSITION_PRESETS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">Font</Label>
          <Select
            value={overlay.fontFamily}
            onValueChange={(v) => onUpdate({ fontFamily: v as TextFontFamily })}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FONT_FAMILIES.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs">Font Size</Label>
          <span className="text-xs text-muted-foreground">{overlay.fontSize}px</span>
        </div>
        <Slider
          value={[overlay.fontSize]}
          onValueChange={([v]) => onUpdate({ fontSize: v })}
          min={12}
          max={72}
          step={1}
          className="mt-1"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor={`color-${overlay.id}`} className="text-xs">Color</Label>
          <div className="flex gap-2 mt-1">
            <input
              id={`color-${overlay.id}`}
              type="color"
              value={overlay.color}
              onChange={(e) => onUpdate({ color: e.target.value })}
              className="h-9 w-9 rounded border border-input cursor-pointer"
              aria-label="Text color"
            />
            <Input
              value={overlay.color}
              onChange={(e) => onUpdate({ color: e.target.value })}
              className="flex-1 font-mono text-xs"
            />
          </div>
        </div>

        <div>
          <Label htmlFor={`stroke-${overlay.id}`} className="text-xs">Outline</Label>
          <div className="flex gap-2 mt-1">
            <input
              id={`stroke-${overlay.id}`}
              type="color"
              value={overlay.strokeColor}
              onChange={(e) => onUpdate({ strokeColor: e.target.value })}
              className="h-9 w-9 rounded border border-input cursor-pointer"
              aria-label="Outline color"
            />
            <Input
              value={overlay.strokeColor}
              onChange={(e) => onUpdate({ strokeColor: e.target.value })}
              className="flex-1 font-mono text-xs"
            />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs">Outline Width</Label>
          <span className="text-xs text-muted-foreground">{overlay.strokeWidth}px</span>
        </div>
        <Slider
          value={[overlay.strokeWidth]}
          onValueChange={([v]) => onUpdate({ strokeWidth: v })}
          min={0}
          max={4}
          step={1}
          className="mt-1"
        />
      </div>
    </div>
  );
}

export default TextOverlayEditor;
